import _ from 'lodash';
import JQuery from 'jquery';
import uuid from 'uuid';

import { API } from './constants';
import EventMg from './EventManager';
import AuthProvider, { needTokenUpdate } from './authProvider';
import Loader from '../components/Loader';
import SessionStorageService from '../services/utilities/SessionStorageService';

const HostURL = API.endpoint;

let needLoadingRequestCount = 0;

const showFullScreenLoading = () => {
  if (needLoadingRequestCount === 0) {
    Loader.start();
  }
  needLoadingRequestCount++;
};

const tryHideFullScreenLoading = () => {
  if (needLoadingRequestCount <= 0) {
    return;
  }
  needLoadingRequestCount--;
  if (needLoadingRequestCount === 0) {
    setTimeout(tryCloseLoading, 100);
  }
};
const tryCloseLoading = () => {
  if (needLoadingRequestCount === 0) {
    Loader.done();
  }
};

/**
 * @description 通信管理クラス
 */
class fetchManager {
  /**
   * @description Ajax共通処理
   * @param {String} type
   * @param {String} path
   * @param {Object} params
   * @param {Function} success_cb
   * @param {Function} error_cb
   */
  async fetch(type, path, params = {}, success_cb, error_cb) {
    if (_.isString(path) && !path.match(/http/)) {
      path = HostURL + path;
    }

    let setting = {
      type,
      url: path,
      data: params,
      dataType: 'json',
      contentType: 'application/json',
    };

    let token = SessionStorageService.getToken();
    if (_.isEmpty(token)) {
      console.error('debug', 'fetchManager.js fetch', 'token is empty.');
      await AuthProvider.instance.redirectLoginPage();
    }

    if (needTokenUpdate(token)) {
      token = await AuthProvider.instance.silentTokenUdate();
    }

    // console.log('fetchManager.js fetch before', token);
    setting['headers'] = {
      Authorization: `Bearer ${token}`,
      ['X-API-REQUEST-ID']: uuid.v4(),
    };

    if (params instanceof FormData) {
      setting.contentType = false;
      setting.processData = false;
    }

    if (_.isObject(params)) {
      if (!('isQuiet' in params)) {
        params.isQuiet = false;
      }
    }
    let ajaxDone = false;
    setTimeout(function() {
      if (!ajaxDone && !params.isQuiet) {
        showFullScreenLoading();
      }
    }, 500);

    /* eslint-disable */
    await JQuery.ajax(setting)
      .done(data => {
        ajaxDone = true;
        tryHideFullScreenLoading();
        success_cb(data);
      })
      .fail((...error) => {
        ajaxDone = true;
        tryHideFullScreenLoading();
        console.error(...error); // エラーを吐く
        error_cb(error);
      });
  }

  /**
   * @description GetメソッドでAjaxを実行する。
   * @param {String} path
   * @param {Object} params
   * @param {String} event_type
   * @returns {Promise}
   */
  GetAjax(path, params, event_type = '') {
    // console.log('fetchManager.js GetAjax', path, params, event_type);
    const timestampedParams = { ...params, _: Date.now() };

    return new Promise(async (resolve, rejects) => {
      await this.fetch('GET', path, timestampedParams, resolve, rejects);
    })
      .then(data => {
        // eventがあれば、eventを通知
        if (event_type) EventMg.trigger(event_type, data);

        return data;
      })
      .catch(error => {
        // error eventがあれば、eventを通知
        if (event_type) EventMg.trigger(event_type + '_error', error);

        throw error;
      });
  }

  /**
   * @description PostメソッドでAjaxを実行する。
   * @param {String} path
   * @param {Object} params
   * @param {String} event_type
   * @returns {Promise}
   */
  PostAjax(path, params, event_type = '') {
    // console.log('fetchManager.js PostAjax', path, params, event_type);
    return new Promise(async (resolve, rejects) => {
      if (!(params instanceof FormData)) {
        params = JSON.stringify(params);
      }
      await this.fetch('POST', path, params, resolve, rejects);
    })
      .then(data => {
        // eventがあれば、eventを通知
        if (event_type) EventMg.trigger(event_type, data);

        return data;
      })
      .catch(error => {
        // error eventがあれば、eventを通知
        if (event_type) EventMg.trigger(event_type + '_error', error);

        throw error;
      });
  }

  /**
   * @description PatchメソッドでAjaxを実行する。
   * @param {String} path
   * @param {Object} params
   * @param {String} event_type
   * @returns {Promise}
   */
  PatchAjax(path, params, event_type = '') {
    // console.log('fetchManager.js PatchAjax', path, params, event_type);
    return new Promise(async (resolve, rejects) => {
      // FormData以外の時
      if (!(params instanceof FormData)) {
        params = JSON.stringify(params);
      }

      await this.fetch('PATCH', path, params, resolve, rejects);
    })
      .then(data => {
        // eventがあれば、eventを通知
        if (event_type) EventMg.trigger(event_type, data);

        return data;
      })
      .catch(error => {
        // error eventがあれば、eventを通知
        if (event_type) EventMg.trigger(event_type + '_error', error);

        throw error;
      });
  }

  /**
   * @description DeleteメソッドでAjaxを実行する。
   * @param {String} path
   * @param {Object} params
   * @param {String} event_type
   * @returns {Promise}
   */
  DeleteAjax(path, params, event_type = '') {
    // console.log('fetchManager.js DeleteAjax', path, params, event_type);
    return new Promise(async (resolve, rejects) => {
      await this.fetch(
        'DELETE',
        path,
        JSON.stringify(params),
        resolve,
        rejects
      );
    })
      .then(data => {
        // eventがあれば、eventを通知
        if (event_type) EventMg.trigger(event_type, data);

        return data;
      })
      .catch(error => {
        // error eventがあれば、eventを通知
        if (event_type) EventMg.trigger(event_type + '_error', error);

        throw error;
      });
  }

  /**
   * @description Get (or PATCH)メソッドでCSVをダウンロードAjaxを実行する。
   * @param {String} path APIのパス
   * @param {Object} params パラメータ
   * @param {String} event_type
   * @param {String} httpMethod HTTPメソッド(GETがデフォルト)
   * @returns {Promise}
   */
  CsvAjax(path, params, event_type = '', httpMethod = 'GET') {
    // console.log('fetchManager.js CsvAjax', path, params, event_type);
    const csvFetch = async (type, path, params, success_cb, error_cb) => {
      if (_.isString(path) && !path.match(/http/)) {
        path = HostURL + path;
      }

      let setting = {
        // HTTP method
        type: type,
        url: path,
        data: params,
        dataType: 'text',
        contentType: 'application/text',
      };

      // PATCHの時の処理（GETと違う）
      if (httpMethod === 'PATCH') {
        // params = JQuery.param({ item: { params, } });
        // FormData以外の時
        if (!(params instanceof FormData)) {
          console.log(params);
          params = JSON.stringify(params);
          console.log(params);
        }

        setting = {
          type: type,
          url: path,
          data: params,
          // 受け取りのCSV
          dataType: 'text',
          // 渡すのはJSON
          contentType: 'application/json',
        };
      }

      let token = SessionStorageService.getToken();
      if (_.isEmpty(token)) {
        console.error('debug', 'fetchManager.js withToken', 'token is empty.');
        await AuthProvider.instance.redirectLoginPage();
      }

      if (needTokenUpdate(token)) {
        token = await AuthProvider.instance.silentTokenUdate();
      }

      setting['headers'] = {
        Authorization: `Bearer ${token}`,
        ['X-API-REQUEST-ID']: uuid.v4(),
      };

      JQuery.ajax(setting)
        .done(data => {
          success_cb(data);
        })
        .fail((...error) => {
          console.error(...error); // エラーを吐く
          error_cb(error);
        });
    };

    return new Promise(async (resolve, rejects) => {
      await csvFetch(httpMethod, path, params, resolve, rejects);
    })
      .then(data => {
        // eventがあれば、eventを通知
        if (event_type) EventMg.trigger(event_type, data);

        return data;
      })
      .catch(error => {
        // error eventがあれば、eventを通知
        if (event_type) EventMg.trigger(event_type + '_error', error);

        throw error;
      });
  }
}

const fetchMg = new fetchManager();

export default fetchMg;



// WEBPACK FOOTER //
// ./src/utilities/fetchManager.js